import { IImageAsset } from '@aytm/app/swidget/base/modules/images/types'

export const IMAGE_ASSET_PATH_FILTER_RESIZE_TO_STRETCH = 'resizest'
export const IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT = 'resizelm'
export const IMAGE_ASSET_PATH_FILTER_RESIZE_TO_FIT = 'resizeft'
export const IMAGE_ASSET_PATH_FILTER_RESIZE_TO_FILL = 'resizefl'
export const IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD = 'resizepd'
export const IMAGE_ASSET_PATH_FILTER_SMART_CROP = 'smart_crop'

export const IMAGE_ASSET_SIZE_SMALL = 150
export const IMAGE_ASSET_SIZE_MIDDLE = 600
export const IMAGE_ASSET_SIZE_MIDDLE_SMALL = 300
export const IMAGE_ASSET_SIZE_BIG = 1920
export const OLD_IMAGE_ASSET_WIDTH = 150
export const OLD_IMAGE_ASSET_HEIGHT = 68
export const OLD_IMAGE_ASSET_WIDTH_MEDIUM = 120
export const OLD_IMAGE_ASSET_HEIGHT_MEDIUM = 120

export const IMAGE_ASSET_PATH_GRAVITY_CENTER = 'c'

// ON CHANGES: please also update this function in public/javascripts/_ui/js/image_resizer.js (legacy code support)
export function getImageAssetPath({
  host = (window as { assets_host?: string }).assets_host || '/',
  width,
  height,
  pathPrefix = 'media/cache',
  path = 'no_image',
  filter = '',
  backgroundColor = 'transparent',
  unsharp = false,
  gravity,
  animated = false,
  rembg = false,
  trim = false,
  json = false,
  padratio = false,
  padratio_width = 0,
  padratio_height = 0,
  status
}: {
  host?: string
  width?: number
  height?: number
  pathPrefix?: string
  path?: string
  filter?: string
  backgroundColor?: string
  unsharp?: boolean
  gravity?: string
  animated?: boolean
  rembg?: boolean
  trim?: boolean
  json?: boolean
  padratio?: boolean
  padratio_width?: number
  padratio_height?: number
  status?: string
} = {}): string {
  if (status && 'ready' !== status) return '/images/processing.svg'
  return `${
    // host
    '/' === host[host.length - 1] ? host : host + '/'
  }${
    // prefix
    pathPrefix
  }/${json ? 'json' : 'resolve'}/${
    // filter
    filter
  }${
    // dimensions
    width && height ? `_${width}x${height}` : ''
  }${
    // backgroundColor
    'none' === backgroundColor ? '' : 'string' === typeof backgroundColor ? `_${backgroundColor.toLowerCase()}` : ''
  }${
    // gravity
    gravity && (IMAGE_ASSET_PATH_FILTER_RESIZE_TO_FILL === filter || IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD === filter)
      ? `_${gravity}`
      : ''
  }${
    // unsharp
    unsharp ? '_us' : ''
  }${
    // animated
    animated ? '_an' : ''
  }${
    // rembg
    rembg ? '_rembg' : ''
  }${
    // trim
    trim ? '_trim' : ''
  }${
    // padratio
    padratio ? `_padratio_${padratio_width}x${padratio_height}` : ''
  }/${
    // path
    path
  }`
}

export function getSmallAssetPath(asset: IImageAsset, fill?: boolean): string {
  return getImageAssetPath({
    width: IMAGE_ASSET_SIZE_SMALL,
    height: IMAGE_ASSET_SIZE_SMALL,
    filter: fill ? IMAGE_ASSET_PATH_FILTER_RESIZE_TO_FILL : IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
    path: asset?.original_path,
    backgroundColor: 'white',
    status: asset?.status
  })
}

export function getOldSmallAssetPath(asset: IImageAsset): string {
  return getImageAssetPath({
    width: OLD_IMAGE_ASSET_WIDTH,
    height: OLD_IMAGE_ASSET_HEIGHT,
    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
    backgroundColor: 'white',
    animated: true,
    path: asset?.original_path,
    status: asset?.status
  })
}

export function getOldMediumAssetPath(asset: IImageAsset): string {
  return getImageAssetPath({
    width: OLD_IMAGE_ASSET_WIDTH_MEDIUM,
    height: OLD_IMAGE_ASSET_HEIGHT_MEDIUM,
    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_AND_PAD,
    backgroundColor: 'white',
    animated: true,
    path: asset?.original_path,
    status: asset?.status
  })
}

export function getOldLargeAssetPath(asset: IImageAsset): string {
  return getImageAssetPath({
    width: IMAGE_ASSET_SIZE_MIDDLE,
    height: IMAGE_ASSET_SIZE_MIDDLE,
    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
    backgroundColor: 'white',
    animated: true,
    path: asset?.original_path,
    status: asset?.status
  })
}

export function getMediumAssetPath(asset: IImageAsset, background?: string): string {
  return getImageAssetPath({
    width: IMAGE_ASSET_SIZE_MIDDLE,
    height: IMAGE_ASSET_SIZE_MIDDLE,
    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
    path: asset?.original_path,
    animated: true,
    backgroundColor: background || 'white',
    status: asset?.status
  })
}

export function getBigAssetPath(asset: IImageAsset, background?: string): string {
  return getImageAssetPath({
    width: IMAGE_ASSET_SIZE_BIG,
    height: IMAGE_ASSET_SIZE_BIG,
    filter: IMAGE_ASSET_PATH_FILTER_RESIZE_TO_LIMIT,
    path: asset?.original_path,
    animated: true,
    backgroundColor: background || 'white',
    status: asset?.status
  })
}

// shapes the asset service can crop to (coords in original-image pixel space)
export type CropShape =
  | { type: 'rect'; x: number; y: number; width: number; height: number }
  | { type: 'ellipse'; cx: number; cy: number; rx: number; ry: number }
  | { type: 'polygon'; points: [number, number][] }

const cropCoordPresent = (...vals: unknown[]): boolean => vals.every(v => null != v && '' !== v)

// build a crop filter token consumed by the asset service: media/cache/resolve/<token>/<path>.
// returns null for an incomplete shape so callers can fall back to the plain image.
// NOTE: image_resizer.js mirrors this for legacy `$ASSETS` consumers — keep both in sync.
export function getCropFilter(shape?: CropShape | null): string | null {
  if (!shape?.type) return null
  switch (shape.type) {
    case 'rect':
      return cropCoordPresent(shape.x, shape.y, shape.width, shape.height)
        ? `crop_rect_${shape.x}x${shape.y}_${shape.width}x${shape.height}`
        : null
    case 'ellipse':
      return cropCoordPresent(shape.cx, shape.cy, shape.rx, shape.ry)
        ? `crop_ellipse_${shape.cx}x${shape.cy}_${shape.rx}x${shape.ry}`
        : null
    case 'polygon': {
      const points = (shape.points || []).filter(([x, y]) => cropCoordPresent(x, y))
      return points.length ? `crop_polygon_${points.map(([x, y]) => `${x}x${y}`).join('_')}` : null
    }
    default:
      return null
  }
}

// ready crop URL. backgroundColor 'none' keeps transparency outside ellipse/polygon AND
// stops getImageAssetPath from appending _WxH/_bg into the token (the service wouldn't match it).
export function getCropAssetPath(shape: CropShape | null, path?: string): string | null {
  const filter = getCropFilter(shape)
  return filter && path ? getImageAssetPath({ filter, path, backgroundColor: 'none' }) : null
}
